"use client";
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  Box, Typography, TextField, IconButton, List, ListItem, ListItemAvatar, 
  ListItemText, Avatar, Paper, InputBase, CircularProgress, Backdrop,
  Tabs, Tab, RadioGroup, FormControlLabel, Radio, Autocomplete, Tooltip,
  Divider
} from '@mui/material';
import { 
  Send as SendIcon, Search as SearchIcon, Add as AddIcon, 
  AttachFile as AttachIcon, GroupOutlined as GroupIcon,
  PersonOutline as PersonIcon, EmailOutlined as EmailIcon, 
  Subject as SubjectIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { QK } from '../../base/qk';
import { searchClients } from '../../api/services/Client/searchClient';
import { searchCandidates } from '../../api/services/candidate';
import GroupConfirmModal from './ConfrimModal/GroupConfirmModal';
import { 
  fetchEmailConversations, 
  fetchEmailHistory, 
  fetchGroupEmailHistory, 
  sendEmail, 
  sendGroupEmail 
} from '../../api/services/TextMessage/emailMessage';

const EmailCommunication = ({ conversationId: initialConvId = null }) => {
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0); // Default to Direct (0)
  const [selectedChat, setSelectedChat] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isComposeMode, setIsComposeMode] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [sendTarget, setSendTarget] = useState('selected');
  const [selectedGroupEmployees, setSelectedGroupEmployees] = useState([]);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  
  const [limit, setLimit] = useState(10);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const scrollRef = useRef(null);

  // 1. Client Search Logic
  const { data: searchResults = [], isFetching: isSearching } = useQuery(
    ['clientEmailSearch', searchTerm],
    () => searchClients(searchTerm),
    { enabled: tabValue === 0 && searchTerm.length >= 2, keepPreviousData: true }
  );

  // 2. Candidate Search Logic (For Group Recipients)
  const { data: candidateResults = [], isFetching: isSearchingCandidates } = useQuery(
    ['candidateEmailSearch', candidateSearchTerm],
    () => searchCandidates(candidateSearchTerm),
    { enabled: tabValue === 1 && sendTarget === 'selected' && candidateSearchTerm.length >= 2, keepPreviousData: true }
  );

  const { data: conversations, isLoading: isListLoading } = useQuery(
    [QK.conversations, 'email'], fetchEmailConversations, { 
      refetchOnWindowFocus: false,
      refetchInterval: 60000 
    }
  );

  // Filter conversations based on tab (Direct vs Group)
  const filteredConversations = useMemo(() => {
    const list = conversations?.data || [];
    if (tabValue === 0) {
      return list.filter(c => c.is_group === false || !c.is_group);
    } else {
      return list.filter(c => c.is_group === true);
    }
  }, [conversations, tabValue]);

  const activeEmail = isComposeMode ? newRecipient : (selectedChat?.chat_partner || initialConvId);
  
  const { 
    data: historyRes, 
    isLoading: isHistoryLoading, 
    isFetching: isHistoryFetching 
  } = useQuery(
    ['emailHistory', activeEmail, tabValue, limit],
    () => tabValue === 1 ? fetchGroupEmailHistory(limit) : fetchEmailHistory(activeEmail, limit),
    { 
        enabled: (!!activeEmail || tabValue === 1) && !isComposeMode, 
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    }
  );

  const mutation = useMutation(
    (payload) => payload.is_group ? sendGroupEmail(payload) : sendEmail(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['emailHistory']);
        setMessage('');
        setSubject('');
        setIsComposeMode(false);
        setIsConfirmModalOpen(false);
        setIsInitialLoad(true); 
      }
    }
  );

  useEffect(() => {
    if (scrollRef.current && isInitialLoad && !isHistoryLoading) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setIsInitialLoad(false);
    }
  }, [historyRes?.data, isInitialLoad, isHistoryLoading]);

  useEffect(() => {
    if (scrollRef.current && !isInitialLoad) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [historyRes?.data?.length, isInitialLoad]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight } = e.target;
    if (scrollTop === 0 && historyRes?.has_more && !isHistoryFetching) {
      const prevHeight = scrollHeight;
      setLimit((prev) => prev + 10);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevHeight;
      }, 150);
    }
  };

  const handleSelectClient = (client) => {
    setNewRecipient(client.primary_email);
    setIsComposeMode(true);
    setSelectedChat(null);
    setSearchTerm('');
  };

  const handleFinalSend = () => {
    const isGroup = tabValue === 1;
    const targetEmail = isComposeMode ? newRecipient : activeEmail;
    const payload = {
        subject: subject,
        body: message,
        is_group: isGroup,
        recipient: isGroup ? null : targetEmail,
        sendTarget: isGroup ? sendTarget : null,
        recipients: isGroup ? (sendTarget === 'all' ? 'ALL_EMPLOYEES' : selectedGroupEmployees.map(emp => emp.primary_email)) : null
    };
    mutation.mutate(payload);
  };

  const showDropdown = tabValue === 0 && searchTerm.length >= 2 && searchResults.length > 0;

  return (
    <Box sx={{ display: 'flex', height: '90vh', m: 1, bgcolor: '#F8F9FB', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(224, 230, 237, 0.5)' }}>
      {/* SIDEBAR */}
      <Box sx={{ width: 360, display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRight: '1px solid #F1F3F5' }}>
        <Box sx={{ p: 3, pb: 1, position: 'relative' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Emails</Typography>
            <IconButton onClick={() => { setIsComposeMode(true); setSelectedChat(null); setSearchTerm(''); }} sx={{ bgcolor: '#EBF5FF', color: '#007FFF' }}>
              <AddIcon />
            </IconButton>
          </Box>
          <Tabs 
            value={tabValue} 
            onChange={(e, v) => { 
                setTabValue(v); 
                setIsComposeMode(false); 
                setSelectedChat(null); 
                setSearchTerm(''); 
                setLimit(10);
            }} 
            sx={{ mb: 2 }}
          >
            <Tab icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Direct" />
            <Tab icon={<GroupIcon sx={{ fontSize: 18 }} />} label="Groups" />
          </Tabs>
          
          <TextField
            fullWidth size="small" 
            placeholder={tabValue === 0 ? "Search clients..." : "Search groups..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: isSearching ? <CircularProgress size={16} sx={{mr:1}}/> : <SearchIcon sx={{ color: '#9CA3AF', mr: 1 }} /> }}
          />

          {showDropdown && (
            <Paper elevation={8} sx={{ position: 'absolute', top: '100%', left: 24, right: 24, zIndex: 1100, borderRadius: '12px', mt: 1, maxHeight: 250, overflowY: 'auto' }}>
              <List>
                {searchResults.map((client) => (
                  <ListItem button key={client.id} onClick={() => handleSelectClient(client)}>
                    <ListItemAvatar><Avatar>{client.full_name?.substring(0,2)}</Avatar></ListItemAvatar>
                    <ListItemText primary={client.full_name} secondary={client.primary_email} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        <List sx={{ flex: 1, overflowY: 'auto', px: 1.5 }}>
          {isListLoading ? <CircularProgress size={20} sx={{ display: 'block', m: 'auto' }}/> :
            filteredConversations.map((chat) => (
              <ListItem 
                button key={chat.id} 
                onClick={() => { setSelectedChat(chat); setIsComposeMode(false); setLimit(10); setIsInitialLoad(true); }}
                sx={{ borderRadius: '12px', mb: 0.5, bgcolor: selectedChat?.id === chat.id ? '#EBF5FF' : 'transparent' }}
              >
                <ListItemAvatar>
                    <Avatar sx={{ bgcolor: chat.is_group ? '#818CF8' : '#10B981' }}>
                        {chat.is_group ? <GroupIcon /> : <EmailIcon />}
                    </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={<Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{chat.chat_partner}</Typography>} 
                  secondary={<Typography variant="caption" noWrap sx={{ display: 'block' }}>{chat.last_subject || chat.body}</Typography>} 
                />
              </ListItem>
            ))
          }
        </List>
      </Box>

      {/* RIGHT AREA */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff', position: 'relative' }}>
        <Backdrop sx={{ zIndex: 10, position: 'absolute' }} open={mutation.isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* HEADER & RECIPIENTS */}
        <Box sx={{ px: 4, py: 2, borderBottom: '1px solid #F3F4F6' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: tabValue === 1 ? '#818CF8' : '#10B981', mr: 2 }}>
                    {tabValue === 1 ? <GroupIcon /> : <EmailIcon />}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    {isComposeMode ? (
                        <InputBase fullWidth placeholder="To: email@example.com" value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)} sx={{ fontWeight: 700 }} />
                    ) : (
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            {selectedChat?.chat_partner || (tabValue === 1 ? "Group Broadcast" : "Select Thread")}
                        </Typography>
                    )}
                </Box>
                {tabValue === 1 && (
                    <RadioGroup row value={sendTarget} onChange={(e) => setSendTarget(e.target.value)}>
                        <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                        <FormControlLabel value="selected" control={<Radio size="small" />} label="Selected" />
                    </RadioGroup>
                )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#F9FAFB', p: 1, borderRadius: '8px', mb: 1 }}>
                <SubjectIcon sx={{ fontSize: 18, color: '#9CA3AF', mr: 1 }} />
                <InputBase fullWidth placeholder="Subject line..." value={subject} onChange={(e) => setSubject(e.target.value)} sx={{ fontSize: '0.9rem', fontWeight: 600 }} />
            </Box>

            {tabValue === 1 && sendTarget === 'selected' && (
                <Autocomplete
                    multiple size="small"
                    options={candidateResults || []}
                    loading={isSearchingCandidates}
                    getOptionLabel={(option) => `${option.full_name} (${option.primary_email})`}
                    filterOptions={(x) => x}
                    onInputChange={(e, value) => setCandidateSearchTerm(value)}
                    onChange={(e, val) => setSelectedGroupEmployees(val)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        variant="standard" 
                        placeholder="Search & Add recipients..." 
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isSearchingCandidates ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                />
            )}
        </Box>

        {/* EMAIL HISTORY */}
        <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, p: 4, overflowY: 'auto', bgcolor: '#FDFDFD' }}>
          {isHistoryFetching && <Box sx={{textAlign:'center'}}><CircularProgress size={20}/></Box>}
          {isHistoryLoading ? <CircularProgress sx={{ display: 'block', m: 'auto' }} /> :
            historyRes?.data?.map((msg, index) => (
                <Box key={index} sx={{ mb: 4, display: 'flex', justifyContent: msg.direction === "OUTBOUND" ? 'flex-end' : 'flex-start' }}>
                    <Paper elevation={0} sx={{ 
                        p: 3, maxWidth: '85%', border: '1px solid #E5E7EB', borderRadius: '12px',
                        bgcolor: msg.direction === "OUTBOUND" ? '#F0F9FF' : '#fff'
                    }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#6B7280', display: 'block', mb: 1 }}>
                            Subject: {msg.subject}
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        <Typography sx={{ fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>{msg.body}</Typography>
                        <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'right', color: '#9CA3AF' }}>{msg.created_at}</Typography>
                    </Paper>
                </Box>
            ))
          }
        </Box>

        {/* INPUT BOX */}
        <Box sx={{ px: 4, py: 3, borderTop: '1px solid #F3F4F6' }}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#F3F4F6', borderRadius: '16px' }}>
            <InputBase 
              multiline rows={4} fullWidth
              placeholder="Write your email content..." 
              value={message} 
              onChange={(e) => setMessage(e.target.value)}
              sx={{ fontSize: '0.9rem' }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, alignItems: 'center' }}>
                <IconButton size="small"><AttachIcon fontSize="small" /></IconButton>
                <IconButton 
                    onClick={() => {
                      if(tabValue === 1) setIsConfirmModalOpen(true);
                      else handleFinalSend();
                    }} 
                    disabled={!message.trim() || !subject.trim()}
                    sx={{ bgcolor: '#10B981', color: '#fff', '&:hover': { bgcolor: '#059669' } }}
                >
                    <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
          </Paper>
        </Box>
      </Box>

      <GroupConfirmModal
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleFinalSend}
        targetType={sendTarget}
        targetCount={selectedGroupEmployees.length}
        message={`Subject: ${subject}\n\n${message}`}
        isLoading={mutation.isLoading}
      />
    </Box>
  );
};

export default EmailCommunication;